import { a as KnownMarketplacesFileSchema, o as PluginManifestSchema, r as InstalledPluginsFileSchema, s as MarketplaceCatalogSchema, t as PluginsConfigFileSchema } from "./plugins-BXY_OlE0.mjs";
import { z } from "zod";
import { access, chmod, copyFile, cp, lstat, mkdir, mkdtemp, readFile, readdir, realpath, rename, rm, rmdir, stat, symlink, unlink, writeFile } from "node:fs/promises";
import { basename, dirname, isAbsolute, join, relative, resolve, sep } from "node:path";
import { Implements, Service, ServiceLifetime, createInterfaceId } from "@gitlab/needle";
import { FileAccessService, FileNotFoundError } from "@gitlab-org/fs";
import { Logger, withPrefix } from "@gitlab-org/logging";
import { randomUUID } from "node:crypto";
import { setTimeout } from "node:timers/promises";
import { getDuoConfigDir } from "@gitlab-org/ai-configuration";
import { simpleGit } from "simple-git";
import { homedir } from "node:os";
import { PluginCommandsSource, PluginSkillsSource } from "@gitlab-org/ai-context/node";
//#region src/fs/write_file_atomic.ts
/**
* Writes `data` to `filePath` by writing to a temp file in the same
* directory and renaming it into place, so a crash mid-write can't leave a
* torn/corrupt file and a concurrent reader never sees a partial write.
*/
async function writeFileAtomic(filePath, data, options) {
	const tmpPath = `${filePath}.${randomUUID()}.tmp`;
	try {
		await writeFile(tmpPath, data, {
			encoding: "utf8",
			mode: options?.mode
		});
		try {
			await rename(tmpPath, filePath);
		} catch (e) {
			if (e.code !== "EPERM") throw e;
			await setTimeout(100);
			await rename(tmpPath, filePath);
		}
	} catch (e) {
		await unlink(tmpPath).catch(() => {});
		throw e;
	}
}
//#endregion
//#region src/schema/parse.ts
/**
* Thrown when untrusted or persisted JSON fails schema validation. The message
* is a human-readable rendering of the zod issues to show the user
*/
var SchemaValidationError = class extends Error {
	constructor(fileName, error) {
		super(`${fileName} is invalid:\n${z.prettifyError(error)}`);
		this.name = "SchemaValidationError";
	}
};
function parseWith(schema, raw, fileName) {
	const result = schema.safeParse(raw);
	if (!result.success) throw new SchemaValidationError(fileName, result.error);
	return result.data;
}
function parseMarketplaceCatalog(raw) {
	return parseWith(MarketplaceCatalogSchema, raw, "marketplace.json");
}
function parseKnownMarketplacesFile(raw) {
	return parseWith(KnownMarketplacesFileSchema, raw, "known_marketplaces.json");
}
function parseInstalledPluginsFile(raw) {
	return parseWith(InstalledPluginsFileSchema, raw, "installed_plugins.json");
}
function parsePluginsConfigFile(raw) {
	return parseWith(PluginsConfigFileSchema, raw, "plugins.json");
}
function parsePluginManifest(raw) {
	return parseWith(PluginManifestSchema, raw, "plugin.json");
}
//#endregion
//#region src/marketplace/paths.ts
function duoConfigDir$1() {
	const dir = getDuoConfigDir();
	if (!dir) throw new Error("Unable to resolve the Duo config directory.");
	return dir;
}
/** `<duoConfigDir>/known_marketplaces.json` — the local registry of added marketplaces. */
function getKnownMarketplacesFilePath() {
	return join(duoConfigDir$1(), "known_marketplaces.json");
}
/** `<duoConfigDir>/marketplaces` — the directory holding cloned/copied catalogs. */
function getMarketplacesDir() {
	return join(duoConfigDir$1(), "marketplaces");
}
/** `<duoConfigDir>/marketplaces/<name>` — a marketplace's install location on disk. */
function getInstallLocation(name) {
	return join(getMarketplacesDir(), name);
}
//#endregion
//#region src/marketplace/known_marketplaces_store.ts
let _initClass$17;
function _applyDecs$17(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$17(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$17(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$17(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$17(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$17(t) {
	var i = _toPrimitive$17(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$17(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$17(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$17(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
const FILE_MODE$2 = 384;
const DIR_MODE$4 = 448;
/**
* Reads and writes the local `known_marketplaces.json` registry. Validates on
* read so a hand-edited or corrupt file surfaces a clear error rather than
* silently dropping the user's marketplaces.
*/
const KnownMarketplacesStore = createInterfaceId("PluginMarketplaceKnownMarketplacesStore");
let _DefaultKnownMarketpl;
(class {
	static {
		[_DefaultKnownMarketpl, _initClass$17] = _applyDecs$17(this, [Implements(KnownMarketplacesStore), Service({
			dependencies: [Logger, FileAccessService],
			lifetime: ServiceLifetime.Singleton
		})], []).c;
	}
	#logger;
	#fileAccess;
	constructor(logger, fileAccess) {
		this.#logger = withPrefix(logger, "[KnownMarketplacesStore]");
		this.#fileAccess = fileAccess;
	}
	async load() {
		const filePath = getKnownMarketplacesFilePath();
		let raw;
		try {
			raw = await this.#fileAccess.getText(filePath);
		} catch (e) {
			if (e instanceof FileNotFoundError) return {};
			throw e;
		}
		return parseKnownMarketplacesFile(JSON.parse(raw));
	}
	async get(name) {
		return (await this.load())[name];
	}
	async upsert(name, entry) {
		const entries = await this.load();
		entries[name] = entry;
		await this.#save(entries);
		this.#logger.info(`Recorded marketplace ${name}`);
	}
	async remove(name) {
		const entries = await this.load();
		delete entries[name];
		await this.#save(entries);
		this.#logger.info(`Removed marketplace ${name}`);
	}
	async exists() {
		try {
			await this.#fileAccess.getText(getKnownMarketplacesFilePath());
			return true;
		} catch (e) {
			if (e instanceof FileNotFoundError) return false;
			throw e;
		}
	}
	async #save(entries) {
		const filePath = getKnownMarketplacesFilePath();
		const dir = dirname(filePath);
		await mkdir(dir, {
			recursive: true,
			mode: DIR_MODE$4
		});
		await chmod(dir, DIR_MODE$4);
		await writeFileAtomic(filePath, `${JSON.stringify(entries, null, 2)}\n`, { mode: FILE_MODE$2 });
		await chmod(filePath, FILE_MODE$2);
	}
	static {
		_initClass$17();
	}
});
//#endregion
//#region src/git/git_clone.ts
/** Result of a successful clone. */
/** Thrown when the underlying `git clone` fails. Wraps git's own message with context. */
var GitCloneError = class extends Error {
	constructor(url, cause) {
		const detail = cause instanceof Error ? cause.message : String(cause);
		super(`Failed to clone "${url}": ${detail}`);
		this.name = "GitCloneError";
		this.cause = cause;
	}
};
/**
* Clone a plugin marketplace git repository using `simple-git`.
*
* We deliberately do NOT define what a valid git URL looks like (user input) -
* `simple-git`'s default unsafe-operations guard blocks the dangerous argument
* classes (`--upload-pack`, `ext::`, config injection), and git itself is the
* authority on whether the URL is valid/fetchable. Anything else surfaces as
* a wrapped error.
*/
async function gitClone({ url, dest, ref }) {
	const git = simpleGit({ unsafe: {
		allowUnsafeEditor: true,
		allowUnsafePager: true,
		allowUnsafeAskPass: true,
		allowUnsafeSshCommand: true,
		allowUnsafeCredentialHelper: true
	} }).env({
		...process.env,
		GIT_TERMINAL_PROMPT: "0"
	});
	const options = ref ? [
		"--depth",
		"1",
		"--branch",
		ref
	] : ["--depth", "1"];
	try {
		await git.clone(url, dest, options);
		return { sha: (await simpleGit(dest).revparse(["HEAD"])).trim() };
	} catch (e) {
		throw new GitCloneError(url, e);
	}
}
//#endregion
//#region src/marketplace/marketplace_fetcher.ts
let _initClass$16;
function _applyDecs$16(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$16(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$16(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$16(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$16(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$16(t) {
	var i = _toPrimitive$16(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$16(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$16(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$16(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
const CATALOG_FILENAMES = ["marketplace.json", join(".claude-plugin", "marketplace.json")];
const DIR_MODE$3 = 448;
async function pathExists$1(target) {
	try {
		await access(target);
		return true;
	} catch {
		return false;
	}
}
/** A fetched, validated marketplace catalog and where it now lives on disk. */
/**
* Fetches a marketplace catalog and makes it available on disk. Owns the full
* fetch lifecycle: staging into a temp dir, validating `marketplace.json`, and
* moving it to its final `marketplaces/<name>/` location.
*/
const MarketplaceFetcher = createInterfaceId("PluginMarketplaceFetcher");
let _DefaultMarketplaceFe;
(class {
	static {
		[_DefaultMarketplaceFe, _initClass$16] = _applyDecs$16(this, [Implements(MarketplaceFetcher), Service({
			dependencies: [Logger],
			lifetime: ServiceLifetime.Singleton
		})], []).c;
	}
	#logger;
	constructor(logger) {
		this.#logger = withPrefix(logger, "[MarketplaceFetcher]");
	}
	async fetch(source, requiredCatalogName) {
		await mkdir(getMarketplacesDir(), {
			recursive: true,
			mode: DIR_MODE$3
		});
		const tempDir = await mkdtemp(join(getMarketplacesDir(), ".tmp-"));
		try {
			const populated = await this.#populate(source, tempDir);
			await rm(join(tempDir, ".git"), {
				recursive: true,
				force: true
			});
			const catalog = await this.readInstalledCatalog(tempDir);
			if (requiredCatalogName !== void 0 && catalog.name !== requiredCatalogName) throw new Error(`Marketplace catalog name changed from "${requiredCatalogName}" to "${catalog.name}"; refusing to update`);
			const installLocation = getInstallLocation(catalog.name);
			await rm(installLocation, {
				recursive: true,
				force: true
			});
			await rename(tempDir, installLocation);
			return {
				catalog,
				installLocation,
				installedRevision: populated?.installedRevision
			};
		} finally {
			await rm(tempDir, {
				recursive: true,
				force: true
			});
		}
	}
	async readInstalledCatalog(dir) {
		const candidates = CATALOG_FILENAMES.map((filename) => join(dir, filename));
		const found = (await Promise.all(candidates.map(async (candidate) => await pathExists$1(candidate) ? candidate : null))).find((candidate) => candidate !== null);
		if (!found) throw new Error(`No marketplace.json found in ${dir} (looked for ${CATALOG_FILENAMES.join(" and ")})`);
		const text = await readFile(found, "utf-8");
		let raw;
		try {
			raw = JSON.parse(text);
		} catch (e) {
			throw new Error(`${found} is not valid JSON: ${e instanceof Error ? e.message : String(e)}`);
		}
		return parseMarketplaceCatalog(raw);
	}
	async #populate(source, destDir) {
		if (source.source === "directory") {
			this.#logger.debug(`Copying marketplace directory into ${destDir}`);
			await cp(source.path, destDir, {
				recursive: true,
				verbatimSymlinks: true
			});
			return;
		}
		this.#logger.debug(`Cloning marketplace ${source.url} into ${destDir}`);
		const { sha } = await gitClone({
			url: source.url,
			dest: destDir,
			ref: source.ref
		});
		return { installedRevision: sha };
	}
	static {
		_initClass$16();
	}
});
//#endregion
//#region src/plugin/plugin_paths.ts
/** The scope a plugin is installed in. */
function parsePluginScope(value) {
	if (value === "user" || value === "project" || value === "local") return value;
	throw new Error(`Invalid scope "${value}": expected "user", "project", or "local"`);
}
/**
* The `projectPath` that identifies an install in the ledger: the workspace for
* `project`/`local` scope, and `undefined` for `user` scope (which is
* workspace-independent). Keeps install and enable/disable keyed consistently.
*/
function projectPathForScope(scope, workspacePath) {
	return scope === "user" ? void 0 : workspacePath;
}
/**
* Marker file written inside a cached version dir once no ledger record
* references it. Its contents are the unix-ms timestamp when the dir became
* unreferenced, starting a grace period before the sweeper reclaims it.
*/
const ORPHANED_MARKER_FILENAME = ".orphaned_at";
function duoConfigDir() {
	const dir = getDuoConfigDir();
	if (!dir) throw new Error("Unable to resolve the Duo config directory.");
	return dir;
}
/** `<duoConfigDir>/plugins` — the shared store holding materialised plugin versions. */
function getPluginStoreRoot() {
	return join(duoConfigDir(), "plugins");
}
/**
* The shared cache location for a plugin version, keyed only by
* `<marketplace>/<plugin>/<version>` so every consumer shares one copy on disk
* (not scoped by workspace).
*/
function getPluginInstallDir(marketplace, plugin, version) {
	return join(getPluginStoreRoot(), marketplace, plugin, version);
}
/**
* Path of the global `installed_plugins.json` ledger. There is one ledger for
* all scopes; each record carries its own `scope`. Lives at the Duo config root.
*/
function getInstalledPluginsFilePath() {
	return join(duoConfigDir(), "installed_plugins.json");
}
/**
* Path of a scope's `plugins.json` config (plugin enablement). User scope lives
* at the Duo config root; project/local scopes live under the workspace's
* `.gitlab/duo/` (`plugins.json` is committed, `plugins.local.json` is ignored).
*/
function getPluginsConfigFilePath(scope, workspacePath) {
	if (scope === "user") return join(duoConfigDir(), "plugins.json");
	if (!workspacePath) throw new Error(`A workspace path is required to resolve the ${scope} plugins.json`);
	return join(workspacePath, ".gitlab", "duo", scope === "local" ? "plugins.local.json" : "plugins.json");
}
//#endregion
//#region src/plugin/installed_plugins_store.ts
let _initClass$15;
function _applyDecs$15(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$15(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$15(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$15(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$15(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$15(t) {
	var i = _toPrimitive$15(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$15(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$15(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$15(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
const FILE_MODE$1 = 384;
const DIR_MODE$2 = 448;
/**
* Two records identify the same install when their scope and `projectPath`
* match. `user`-scope records carry no `projectPath`, so scope alone
* distinguishes them; `project`/`local` records are keyed per workspace.
*/
function sameInstall(record, scope, projectPath) {
	return record.scope === scope && record.projectPath === projectPath;
}
/**
* Reads and writes the global `installed_plugins.json` ledger, keyed by
* `<plugin>@<marketplace>`. Each id maps to an array of records, one per scope
* the plugin is installed in. Validates on read so a hand-edited or corrupt
* file surfaces a clear error rather than silently dropping installed plugins.
*/
const InstalledPluginsStore = createInterfaceId("PluginMarketplaceInstalledPluginsStore");
let _DefaultInstalledPlug;
(class {
	static {
		[_DefaultInstalledPlug, _initClass$15] = _applyDecs$15(this, [Implements(InstalledPluginsStore), Service({
			dependencies: [Logger, FileAccessService],
			lifetime: ServiceLifetime.Singleton
		})], []).c;
	}
	#logger;
	#fileAccess;
	constructor(logger, fileAccess) {
		this.#logger = withPrefix(logger, "[InstalledPluginsStore]");
		this.#fileAccess = fileAccess;
	}
	async load() {
		const filePath = getInstalledPluginsFilePath();
		let raw;
		try {
			raw = await this.#fileAccess.getText(filePath);
		} catch (e) {
			if (e instanceof FileNotFoundError) return {};
			throw e;
		}
		return parseInstalledPluginsFile(JSON.parse(raw)).plugins;
	}
	async get(id, scope, projectPath) {
		return ((await this.load())[id] ?? []).find((record) => sameInstall(record, scope, projectPath));
	}
	async upsert(id, record) {
		const plugins = await this.load();
		const existing = (plugins[id] ?? []).filter((r) => !sameInstall(r, record.scope, record.projectPath));
		existing.push(record);
		plugins[id] = existing;
		await this.save(plugins);
		this.#logger.info(`Recorded plugin ${id} in ${record.scope} scope`);
	}
	async remove(id, scope, projectPath) {
		const plugins = await this.load();
		const existing = plugins[id] ?? [];
		const remaining = existing.filter((r) => !sameInstall(r, scope, projectPath));
		if (remaining.length === existing.length) return;
		if (remaining.length > 0) plugins[id] = remaining;
		else delete plugins[id];
		await this.save(plugins);
		this.#logger.info(`Removed plugin ${id} from ${scope} scope`);
	}
	async save(plugins) {
		const filePath = getInstalledPluginsFilePath();
		await mkdir(dirname(filePath), {
			recursive: true,
			mode: DIR_MODE$2
		});
		await writeFileAtomic(filePath, `${JSON.stringify({
			version: 1,
			plugins
		}, null, 2)}\n`, { mode: FILE_MODE$1 });
	}
	static {
		_initClass$15();
	}
});
//#endregion
//#region src/plugin/plugin_errors.ts
/** Where an install of a given plugin id exists, for reporting alongside a not-found error. */
var PluginNotInstalledError = class extends Error {
	/** Installs of this plugin id that DO exist, for other scopes/projects. */
	constructor(id, scope, projectPath, installs = []) {
		const location = scope !== "user" && projectPath ? ` in ${projectPath}` : "";
		super(`Plugin ${id} is not installed with ${scope} scope${location}`);
		this.name = "PluginNotInstalledError";
		this.id = id;
		this.scope = scope;
		this.projectPath = projectPath;
		this.installs = installs;
	}
};
var AmbiguousPluginNameError = class extends Error {
	constructor(name, candidateIds) {
		super(`Plugin name ${name} matches multiple installed plugins: ${candidateIds.join(", ")}. Use the full <plugin>@<marketplace> id.`);
		this.name = "AmbiguousPluginNameError";
	}
};
//#endregion
//#region src/plugin/ledger_references.ts
/**
* The set of cache dirs the ledger still points at, as `resolve()`-normalised
* absolute paths.
*
* Normalisation is textual `resolve()` only, never `realpath`: the paths may
* already be gone from disk, where `realpath` would throw. The consequence is
* that callers must build the path they test from the same base as the ledger
* records (`getPluginStoreRoot()`); comparing against a path reached by a
* different route — a symlink, a different mount — reports a live dir as
* unreferenced and gets it reclaimed.
*
* This is the shared reference count behind the orphan lifecycle: the orphaner
* marks a dir once nothing references it, and the sweeper reclaims it after the
* grace period. Both must agree on what "referenced" means, so both go through
* here.
*/
function referencedInstallPaths(ledger) {
	const paths = /* @__PURE__ */ new Set();
	for (const records of Object.values(ledger)) for (const record of records) paths.add(resolve(record.installPath));
	return paths;
}
function isInstallPathReferenced(ledger, installPath) {
	return referencedInstallPaths(ledger).has(resolve(installPath));
}
//#endregion
//#region src/plugin/cache_fs.ts
async function pathExists(target) {
	try {
		await stat(target);
		return true;
	} catch {
		return false;
	}
}
//#endregion
//#region src/plugin/path_containment.ts
/**
* True when `candidate` is `root` itself or a descendant of it.
*
* This is a pure string comparison and never touches the filesystem. Callers
* are responsible for resolving both paths first: use `realpath` for paths that
* exist (e.g. a source tree) and `resolve` for paths that do not yet exist
* (e.g. a copy destination, where `realpath` would throw ENOENT). When you need
* resolve-then-contain for an existing path, use `SafePluginFs.assertWithinStore`
* rather than resolving by hand.
*
* This is the security-critical containment primitive shared by the copy engine
* and the source resolver, so it lives in one place to keep the two in sync.
*/
function isWithin(root, candidate) {
	if (candidate === root) return true;
	const rel = relative(root, candidate);
	return rel.length > 0 && rel !== ".." && !rel.startsWith(`..${sep}`);
}
/** Thrown when a resolved path fails the plugin store containment check. */
var PluginStoreEscapeError = class extends Error {
	constructor(realPath) {
		super(`Refusing to use "${realPath}": outside the plugin store`);
		this.name = "PluginStoreEscapeError";
	}
};
//#endregion
//#region src/plugin/path_segment.ts
const SAFE_SEGMENT = /^[a-zA-Z0-9._-]+$/;
/**
* Validates a single untrusted path segment (marketplace name, plugin name,
* source subdir, skill dir name). Rejects anything that could escape the
* intended directory or be interpreted as a flag.
*/
function isValidPathSegment(segment) {
	if (typeof segment !== "string" || segment.length === 0) return false;
	if (segment === "." || segment === "..") return false;
	if (segment.startsWith("-")) return false;
	for (const char of segment) if (char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127) return false;
	return SAFE_SEGMENT.test(segment);
}
function assertPathSegment(segment, label) {
	if (!isValidPathSegment(segment)) throw new Error(`Invalid ${label}: ${JSON.stringify(segment)} is not a safe path segment`);
}
//#endregion
//#region src/plugin/safe_plugin_fs.ts
let _initClass$14;
function _applyDecs$14(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$14(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$14(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$14(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$14(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$14(t) {
	var i = _toPrimitive$14(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$14(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$14(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$14(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
const SafePluginFs = createInterfaceId("PluginMarketplaceSafePluginFs");
/**
* Immutable roots plus the mutable running state threaded through a single copy
* operation. Built once per `placePluginFiles` so the recursive walk only needs
* to pass the current source/dest dir and depth.
*/
const DEFAULT_MAX_FILES = 2e3;
const DEFAULT_MAX_BYTES = 50 * 1024 * 1024;
const DEFAULT_MAX_DEPTH = 12;
const DIR_MODE$1 = 448;
let _DefaultSafePluginFs;
(class {
	static {
		[_DefaultSafePluginFs, _initClass$14] = _applyDecs$14(this, [Implements(SafePluginFs), Service({
			dependencies: [Logger],
			lifetime: ServiceLifetime.Singleton
		})], []).c;
	}
	#logger;
	constructor(logger) {
		this.#logger = withPrefix(logger, "[SafePluginFs]");
	}
	async placePluginFiles(sourceRoot, destRoot, opts) {
		const limits = {
			maxFiles: opts?.maxFiles ?? DEFAULT_MAX_FILES,
			maxBytes: opts?.maxBytes ?? DEFAULT_MAX_BYTES,
			maxDepth: opts?.maxDepth ?? DEFAULT_MAX_DEPTH
		};
		const pluginRoot = await realpath(sourceRoot);
		const marketplaceRoot = opts?.marketplaceRoot ? await realpath(opts.marketplaceRoot) : pluginRoot;
		const storeRoot = resolve(getPluginStoreRoot());
		const resolvedDest = resolve(destRoot);
		if (resolvedDest === storeRoot || !isWithin(storeRoot, resolvedDest)) throw new Error(`Refusing to install: destination "${resolvedDest}" escapes the plugin store`);
		const parent = dirname(resolvedDest);
		await mkdir(parent, {
			recursive: true,
			mode: DIR_MODE$1
		});
		const realStoreRoot = await realpath(getPluginStoreRoot());
		const realDest = join(await realpath(parent), basename(resolvedDest));
		if (realDest === realStoreRoot || !isWithin(realStoreRoot, realDest)) throw new Error(`Refusing to install: destination "${realDest}" escapes the plugin store`);
		const stagingDir = await mkdtemp(join(parent, `.${basename(resolvedDest)}.tmp-`));
		const realStagingDir = await realpath(stagingDir);
		const ctx = {
			pluginRoot,
			marketplaceRoot,
			stagingRoot: realStagingDir,
			limits,
			counters: {
				files: 0,
				bytes: 0
			},
			written: []
		};
		try {
			await this.#copyDir(ctx, pluginRoot, realStagingDir, 0);
			await this.#swapIntoPlace(stagingDir, resolvedDest);
		} catch (e) {
			await rm(stagingDir, {
				recursive: true,
				force: true
			});
			throw e;
		}
		return ctx.written.map((path) => join(resolvedDest, relative(realStagingDir, path)));
	}
	async #swapIntoPlace(stagingDir, dest) {
		if (!await pathExists(dest)) {
			await rename(stagingDir, dest);
			return;
		}
		const backupDir = `${dest}.old-${process.pid}-${Date.now()}`;
		await rename(dest, backupDir);
		try {
			await rename(stagingDir, dest);
		} catch (e) {
			try {
				await rename(backupDir, dest);
			} catch (restoreError) {
				this.#logger.warn(`Failed to restore previous install from ${backupDir}; leaving it there`, restoreError instanceof Error ? restoreError : new Error(String(restoreError)));
			}
			throw e;
		}
		await rm(backupDir, {
			recursive: true,
			force: true
		});
	}
	async #copyDir(ctx, sourceDir, destDir, depth) {
		if (depth > ctx.limits.maxDepth) throw new Error(`Plugin tree exceeds maximum depth of ${ctx.limits.maxDepth} (at ${sourceDir})`);
		const entries = await readdir(sourceDir, { withFileTypes: true });
		for (const entry of entries) {
			assertPathSegment(entry.name, "plugin file name");
			const sourceEntry = join(sourceDir, entry.name);
			const destEntry = join(destDir, entry.name);
			if (entry.isSymbolicLink()) await this.#copySymlink(ctx, sourceEntry, destEntry, depth);
			else if (entry.isDirectory()) await this.#descend(ctx, sourceEntry, destEntry, depth);
			else if (entry.isFile()) {
				const fileStat = await lstat(sourceEntry);
				await this.#copyOneFile(ctx, sourceEntry, destEntry, fileStat.size, fileStat.mode);
			}
		}
	}
	async #copySymlink(ctx, sourceEntry, destEntry, depth) {
		let resolvedTarget;
		try {
			resolvedTarget = await realpath(sourceEntry);
		} catch (e) {
			this.#logger.warn(`Skipping unresolvable symlink ${sourceEntry}`, e instanceof Error ? e : new Error(String(e)));
			return;
		}
		if (!isWithin(ctx.marketplaceRoot, resolvedTarget)) {
			this.#logger.warn(`Skipping symlink ${sourceEntry} -> ${resolvedTarget} (escapes marketplace ${ctx.marketplaceRoot})`);
			return;
		}
		if (resolvedTarget === ctx.pluginRoot || isWithin(ctx.pluginRoot, resolvedTarget)) {
			await this.#preserveSymlink(ctx, resolvedTarget, destEntry);
			return;
		}
		const targetStat = await stat(resolvedTarget);
		if (targetStat.isDirectory()) {
			const linkParent = await realpath(dirname(sourceEntry));
			if (linkParent === resolvedTarget || isWithin(resolvedTarget, linkParent)) {
				this.#logger.warn(`Skipping cyclic directory symlink ${sourceEntry} -> ${resolvedTarget}`);
				return;
			}
			await this.#descendResolved(ctx, resolvedTarget, destEntry, depth);
		} else if (targetStat.isFile()) await this.#copyOneFile(ctx, resolvedTarget, destEntry, targetStat.size, targetStat.mode);
	}
	async #preserveSymlink(ctx, resolvedTarget, destEntry) {
		ctx.counters.files += 1;
		if (ctx.counters.files > ctx.limits.maxFiles) throw new Error(`Plugin tree exceeds maximum file count of ${ctx.limits.maxFiles}`);
		const targetInDest = join(ctx.stagingRoot, relative(ctx.pluginRoot, resolvedTarget));
		if (!isWithin(ctx.stagingRoot, resolve(targetInDest))) throw new Error(`Preserved symlink ${destEntry} target escapes the plugin store`);
		const linkValue = relative(dirname(destEntry), targetInDest) || ".";
		await mkdir(dirname(destEntry), {
			recursive: true,
			mode: DIR_MODE$1
		});
		await symlink(linkValue, destEntry);
		ctx.written.push(destEntry);
	}
	async #descend(ctx, sourceEntry, destEntry, depth) {
		const realSourceEntry = await realpath(sourceEntry);
		if (!isWithin(ctx.marketplaceRoot, realSourceEntry)) throw new Error(`Source directory ${realSourceEntry} escapes marketplace root ${ctx.marketplaceRoot}`);
		await this.#descendResolved(ctx, realSourceEntry, destEntry, depth);
	}
	async #descendResolved(ctx, realSourceDir, destEntry, depth) {
		if (!isWithin(ctx.stagingRoot, resolve(destEntry))) throw new Error(`Destination directory ${destEntry} escapes the plugin store`);
		await mkdir(destEntry, {
			recursive: true,
			mode: DIR_MODE$1
		});
		await this.#copyDir(ctx, realSourceDir, destEntry, depth + 1);
	}
	async #copyOneFile(ctx, sourceFile, destFile, size, mode) {
		ctx.counters.files += 1;
		if (ctx.counters.files > ctx.limits.maxFiles) throw new Error(`Plugin tree exceeds maximum file count of ${ctx.limits.maxFiles}`);
		ctx.counters.bytes += size;
		if (ctx.counters.bytes > ctx.limits.maxBytes) throw new Error(`Plugin tree exceeds maximum total size of ${ctx.limits.maxBytes} bytes`);
		const resolvedDest = resolve(destFile);
		if (!isWithin(ctx.stagingRoot, resolvedDest)) throw new Error(`Destination file ${destFile} escapes the plugin store`);
		await mkdir(dirname(resolvedDest), {
			recursive: true,
			mode: DIR_MODE$1
		});
		await copyFile(sourceFile, resolvedDest);
		await this.#chmodFile(resolvedDest, mode);
		ctx.written.push(resolvedDest);
	}
	async #chmodFile(filePath, mode) {
		try {
			await chmod(filePath, 384 | mode & 64);
		} catch (e) {
			this.#logger.warn(`Failed to set file mode on ${filePath}`, e instanceof Error ? e : new Error(String(e)));
		}
	}
	async removeFiles(paths) {
		const storeRoot = resolve(getPluginStoreRoot());
		const parentSets = await Promise.all(paths.map(async (filePath) => {
			const resolved = resolve(filePath);
			if (!isWithin(storeRoot, resolved)) {
				this.#logger.warn(`Refusing to remove "${resolved}": outside the plugin store`);
				return [];
			}
			try {
				await rm(resolved, { force: true });
			} catch (e) {
				this.#logger.warn(`Failed to remove ${resolved}`, e instanceof Error ? e : new Error(String(e)));
			}
			const parents = [];
			let parent = dirname(resolved);
			while (isWithin(storeRoot, parent) && parent !== storeRoot) {
				parents.push(parent);
				parent = dirname(parent);
			}
			return parents;
		}));
		const ordered = [...new Set(parentSets.flat())].sort((a, b) => b.length - a.length);
		for (const dir of ordered) await this.#pruneIfEmpty(dir);
	}
	async #pruneIfEmpty(dir) {
		try {
			if ((await readdir(dir)).length === 0) await rmdir(dir);
		} catch (e) {
			if (e.code !== "ENOENT") this.#logger.warn(`Failed to prune directory ${dir}`, e instanceof Error ? e : new Error(String(e)));
		}
	}
	async assertWithinStore(dir) {
		if ((await lstat(dir)).isSymbolicLink()) throw new Error(`Refusing to use "${dir}": it is a symlink`);
		const realDir = await realpath(dir);
		if (!await this.isWithinStore(realDir)) throw new PluginStoreEscapeError(realDir);
		return realDir;
	}
	async isWithinStore(realPath) {
		return isWithin(await realpath(getPluginStoreRoot()).catch(() => resolve(getPluginStoreRoot())), realPath);
	}
	async listCachedFiles(installDir) {
		const realDir = await this.assertWithinStore(installDir);
		const found = [];
		await this.#walkFiles(realDir, found);
		return found.filter((path) => path !== join(realDir, ORPHANED_MARKER_FILENAME));
	}
	async #walkFiles(dir, out) {
		const entries = await readdir(dir, { withFileTypes: true });
		await Promise.all(entries.map(async (entry) => {
			const full = join(dir, entry.name);
			if (entry.isDirectory()) await this.#walkFiles(full, out);
			else if (entry.isFile() || entry.isSymbolicLink()) out.push(full);
		}));
	}
	async writeOrphanMarker(dir) {
		await writeFile(join(await this.assertWithinStore(dir), ORPHANED_MARKER_FILENAME), Date.now().toString(), { mode: 384 });
	}
	async clearOrphanMarker(dir) {
		const realDir = await this.assertWithinStore(dir);
		try {
			await rm(join(realDir, ORPHANED_MARKER_FILENAME));
		} catch (e) {
			if (e.code !== "ENOENT") throw e;
		}
	}
	async readOrphanMarkerTime(dir) {
		const realDir = await this.assertWithinStore(dir);
		let raw;
		try {
			raw = await readFile(join(realDir, ORPHANED_MARKER_FILENAME), "utf8");
		} catch (e) {
			if (e.code === "ENOENT") return;
			throw e;
		}
		const parsed = Number.parseInt(raw.trim(), 10);
		return Number.isFinite(parsed) ? parsed : void 0;
	}
	static {
		_initClass$14();
	}
});
//#endregion
//#region src/plugin/plugin_cache_orphaner.ts
let _initClass$13;
function _applyDecs$13(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$13(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$13(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$13(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$13(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$13(t) {
	var i = _toPrimitive$13(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$13(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$13(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$13(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
/**
* The shared cache dir at `installPath` is reference-counted through the
* installed-plugins ledger. Once the caller has removed the last record
* pointing at it, write the orphan marker so a later sweep can reclaim it
* after the grace period.
*/
const PluginCacheOrphaner = createInterfaceId("PluginMarketplaceCacheOrphaner");
let _DefaultPluginCacheOr;
(class {
	static {
		[_DefaultPluginCacheOr, _initClass$13] = _applyDecs$13(this, [Implements(PluginCacheOrphaner), Service({
			dependencies: [
				Logger,
				InstalledPluginsStore,
				SafePluginFs
			],
			lifetime: ServiceLifetime.Singleton
		})], []).c;
	}
	#logger;
	#store;
	#safeFs;
	constructor(logger, store, safeFs) {
		this.#logger = withPrefix(logger, "[PluginCacheOrphaner]");
		this.#store = store;
		this.#safeFs = safeFs;
	}
	async markOrphanedIfUnreferenced(installPath) {
		try {
			if (isInstallPathReferenced(await this.#store.load(), installPath)) return;
			await this.#safeFs.writeOrphanMarker(installPath);
		} catch (e) {
			if (typeof e === "object" && e !== null && "code" in e && e.code === "ENOENT") {
				this.#logger.debug(`Install dir already gone, nothing to orphan: ${installPath}`);
				return;
			}
			this.#logger.warn(`Could not mark cache dir orphaned at ${installPath}`, e instanceof Error ? e : new Error(String(e)));
		}
	}
	static {
		_initClass$13();
	}
});
//#endregion
//#region src/plugin/plugins_config_store.ts
let _initClass$12;
function _applyDecs$12(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$12(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$12(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$12(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$12(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$12(t) {
	var i = _toPrimitive$12(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$12(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$12(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$12(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
const FILE_MODE = 384;
const DIR_MODE = 448;
/**
* Reads and writes a scope's `plugins.json` config: which plugins are enabled
* and which marketplaces are known. Validates on read so a hand-edited or
* corrupt file surfaces a clear error rather than silently losing config.
*/
const PluginsConfigStore = createInterfaceId("PluginMarketplacePluginsConfigStore");
let _DefaultPluginsConfig;
(class {
	static {
		[_DefaultPluginsConfig, _initClass$12] = _applyDecs$12(this, [Implements(PluginsConfigStore), Service({
			dependencies: [Logger, FileAccessService],
			lifetime: ServiceLifetime.Singleton
		})], []).c;
	}
	#logger;
	#fileAccess;
	constructor(logger, fileAccess) {
		this.#logger = withPrefix(logger, "[PluginsConfigStore]");
		this.#fileAccess = fileAccess;
	}
	async load(scope, workspacePath) {
		const filePath = getPluginsConfigFilePath(scope, workspacePath);
		let raw;
		try {
			raw = await this.#fileAccess.getText(filePath);
		} catch (e) {
			if (e instanceof FileNotFoundError) return {};
			throw e;
		}
		return parsePluginsConfigFile(JSON.parse(raw));
	}
	async getExistingEnabledSetting(id, scope, workspacePath) {
		const ladder = [
			"local",
			"project",
			"user"
		];
		const scopes = ladder.slice(ladder.indexOf(scope)).filter((candidate) => candidate === "user" || workspacePath !== void 0);
		for (const candidate of scopes) {
			const setting = (await this.load(candidate, workspacePath)).enabledPlugins?.[id];
			if (setting !== void 0) return setting;
		}
	}
	async setEnabled(id, enabled, scope, workspacePath) {
		const config = await this.load(scope, workspacePath);
		const enabledPlugins = config.enabledPlugins ?? {};
		enabledPlugins[id] = enabled;
		config.enabledPlugins = enabledPlugins;
		await this.#save(config, scope, workspacePath);
		this.#logger.info(`Set ${id} enabled=${enabled} in ${scope} scope`);
	}
	async clearEnabled(id, scope, workspacePath) {
		const config = await this.load(scope, workspacePath);
		if (!config.enabledPlugins || !(id in config.enabledPlugins)) return;
		delete config.enabledPlugins[id];
		await this.#save(config, scope, workspacePath);
		this.#logger.info(`Cleared enabled flag for ${id} in ${scope} scope`);
	}
	async #save(config, scope, workspacePath) {
		const filePath = getPluginsConfigFilePath(scope, workspacePath);
		await mkdir(dirname(filePath), {
			recursive: true,
			mode: DIR_MODE
		});
		await writeFile(filePath, `${JSON.stringify(config, null, 2)}\n`, { mode: FILE_MODE });
	}
	static {
		_initClass$12();
	}
});
//#endregion
//#region src/plugin/plugin_specifier.ts
/** A parsed `<plugin>@<marketplace>` specifier. */
/**
* Parse a `<plugin>@<marketplace>` specifier (split on the last `@`). Both
* parts are validated as safe path segments.
*/
function parseSpecifier(raw) {
	const at = raw.lastIndexOf("@");
	if (at <= 0 || at === raw.length - 1) throw new Error(`Invalid plugin specifier ${JSON.stringify(raw)}: expected "<plugin>@<marketplace>"`);
	const plugin = raw.slice(0, at);
	const marketplace = raw.slice(at + 1);
	assertPathSegment(plugin, "plugin name");
	assertPathSegment(marketplace, "marketplace name");
	return {
		plugin,
		marketplace
	};
}
//#endregion
//#region src/plugin/plugin_registry.ts
let _initClass$11;
function _applyDecs$11(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$11(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$11(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$11(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$11(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$11(t) {
	var i = _toPrimitive$11(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$11(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$11(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$11(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
const SCOPE_ORDER$1 = [
	"user",
	"project",
	"local"
];
/** A resolved installed-plugin entry joining the ledger record with enabled-state. */
/** The outcome of a successful enable/disable/uninstall: the resolved id and record's version. */
/** Manages the set of installed plugins and their enabled-state. */
const PluginRegistry = createInterfaceId("PluginMarketplacePluginRegistry");
let _DefaultPluginRegistr;
(class {
	static {
		[_DefaultPluginRegistr, _initClass$11] = _applyDecs$11(this, [Implements(PluginRegistry), Service({
			dependencies: [
				Logger,
				InstalledPluginsStore,
				PluginsConfigStore,
				PluginCacheOrphaner
			],
			lifetime: ServiceLifetime.Singleton
		})], []).c;
	}
	#logger;
	#store;
	#config;
	#cacheOrphaner;
	constructor(logger, store, config, cacheOrphaner) {
		this.#logger = withPrefix(logger, "[PluginRegistry]");
		this.#store = store;
		this.#config = config;
		this.#cacheOrphaner = cacheOrphaner;
	}
	async enable(id, scope, workspacePath) {
		return this.#setEnabled(id, true, scope, workspacePath);
	}
	async disable(id, scope, workspacePath) {
		return this.#setEnabled(id, false, scope, workspacePath);
	}
	async uninstall(id, scope, workspacePath) {
		const projectPath = projectPathForScope(scope, workspacePath);
		const resolvedId = await this.#resolveId(id, scope, projectPath);
		const record = await this.#store.get(resolvedId, scope, projectPath);
		if (!record) throw await this.#notInstalledError(resolvedId, scope, projectPath);
		await this.#store.remove(resolvedId, scope, projectPath);
		try {
			await this.#config.clearEnabled(resolvedId, scope, workspacePath);
		} finally {
			await this.#cacheOrphaner.markOrphanedIfUnreferenced(record.installPath);
		}
		return {
			id: resolvedId,
			version: record.version
		};
	}
	async #setEnabled(id, enabled, scope, workspacePath) {
		const projectPath = projectPathForScope(scope, workspacePath);
		const resolvedId = await this.#resolveId(id, scope, projectPath);
		const record = await this.#store.get(resolvedId, scope, projectPath);
		if (!record) throw await this.#notInstalledError(resolvedId, scope, projectPath);
		await this.#config.setEnabled(resolvedId, enabled, scope, workspacePath);
		return {
			id: resolvedId,
			version: record.version
		};
	}
	async #notInstalledError(id, scope, projectPath) {
		return new PluginNotInstalledError(id, scope, projectPath, ((await this.#store.load())[id] ?? []).map((record) => ({
			scope: record.scope,
			projectPath: record.projectPath,
			version: record.version
		})));
	}
	async #resolveId(input, scope, projectPath) {
		if (input.includes("@")) return input;
		const ledger = await this.#store.load();
		const nameMatches = Object.entries(ledger).filter(([candidateId]) => {
			let plugin;
			try {
				({plugin} = parseSpecifier(candidateId));
			} catch {
				return false;
			}
			return plugin === input;
		});
		const candidates = nameMatches.filter(([, records]) => records.some((record) => record.scope === scope && record.projectPath === projectPath)).map(([candidateId]) => candidateId);
		if (candidates.length === 1) return candidates[0];
		if (candidates.length > 1) throw new AmbiguousPluginNameError(input, candidates);
		throw new PluginNotInstalledError(input, scope, projectPath, nameMatches.flatMap(([, records]) => records.map((record) => ({
			scope: record.scope,
			projectPath: record.projectPath,
			version: record.version
		}))));
	}
	async list(opts) {
		const ledger = await this.#store.load();
		const pairs = Object.entries(ledger).flatMap(([id, records]) => records.map((record) => [id, record]));
		const configProjectPath = (record) => record.scope === "user" ? void 0 : record.projectPath ?? opts?.workspacePath;
		const configKey = (scope, projectPath) => `${scope}:${projectPath ?? ""}`;
		const scopeProjectPairs = /* @__PURE__ */ new Map();
		for (const [, record] of pairs) {
			const projectPath = configProjectPath(record);
			scopeProjectPairs.set(configKey(record.scope, projectPath), {
				scope: record.scope,
				projectPath
			});
		}
		const enabledEntries = await Promise.all([...scopeProjectPairs.values()].map(async ({ scope, projectPath }) => {
			const key = configKey(scope, projectPath);
			try {
				return [key, (await this.#config.load(scope, projectPath)).enabledPlugins ?? {}];
			} catch (e) {
				this.#logger.warn(`Could not read plugins config for scope "${scope}"${projectPath ? ` (${projectPath})` : ""}, defaulting to enabled`, e instanceof Error ? e : new Error(String(e)));
				return [key, {}];
			}
		}));
		const enabledMaps = new Map(enabledEntries);
		const plugins = pairs.map(([id, record]) => {
			const projectPath = configProjectPath(record);
			const enabled = (enabledMaps.get(configKey(record.scope, projectPath)) ?? {})[id] ?? true;
			return {
				id,
				version: record.version,
				scope: record.scope,
				enabled,
				installPath: record.installPath,
				installedAt: record.installedAt,
				lastUpdated: record.lastUpdated,
				projectPath: record.projectPath
			};
		});
		plugins.sort((a, b) => {
			const scopeDiff = SCOPE_ORDER$1.indexOf(a.scope) - SCOPE_ORDER$1.indexOf(b.scope);
			if (scopeDiff !== 0) return scopeDiff;
			return a.id.localeCompare(b.id);
		});
		return plugins;
	}
	static {
		_initClass$11();
	}
});
//#endregion
//#region src/marketplace/marketplace_errors.ts
var MarketplaceNotFoundError = class extends Error {
	constructor(name) {
		super(`Marketplace ${name} is not registered`);
		this.name = "MarketplaceNotFoundError";
	}
};
//#endregion
//#region src/marketplace/sources/directory_source.ts
/** Expand a leading `~` to the user's home directory. */
function expandHome(input) {
	if (input === "~") return homedir();
	if (input.startsWith("~/")) return join(homedir(), input.slice(2));
	return input;
}
/** Directory source: the input (after `~` expansion) is an existing local directory. */
const resolveDirectorySource = async (input) => {
	const expanded = expandHome(input);
	const absolute = isAbsolute(expanded) ? expanded : resolve(expanded);
	try {
		if ((await stat(absolute)).isDirectory()) return {
			source: "directory",
			path: absolute
		};
	} catch (e) {
		const { code } = e;
		if (code === "EACCES" || code === "EPERM") throw new Error(`Cannot access marketplace source "${input}": permission denied (${absolute})`);
	}
	return null;
};
//#endregion
//#region src/marketplace/sources/url_source.ts
/** A full URL (`scheme://...`) or scp-like (`user@host:path`) — git's `@` is structural, not a ref. */
function isUrlLike(input) {
	return input.includes("://") || /^[^/]+@[^/]+:/.test(input);
}
function splitOn(input, sep) {
	const i = sep === "#" ? input.indexOf(sep) : input.lastIndexOf(sep);
	if (i <= 0) return { base: input };
	return {
		base: input.slice(0, i),
		ref: input.slice(i + 1) || void 0
	};
}
/** URLs pin with `#ref`; shorthand/paths pin with `@ref`. */
function splitRef(input) {
	if (input.includes("#")) return splitOn(input, "#");
	return isUrlLike(input) ? { base: input } : splitOn(input, "@");
}
/**
* URL source: the catch-all. Emits a `url` marketplace source, git-cloned by the
* fetcher — `url` means "a URL git can clone", not an HTTP download. We do not
* validate the URL shape; git is the authority.
*/
const resolveUrlSource = async (input) => {
	const { base, ref } = splitRef(input);
	return ref ? {
		source: "url",
		url: base,
		ref
	} : {
		source: "url",
		url: base
	};
};
//#endregion
//#region src/marketplace/sources/source_resolver.ts
/**
* Recognises one shape of `<source>` input and returns a {@link MarketplaceSource},
* or `null` if it does not apply. Resolvers are tried in order; the first
* non-null result wins. To support a new source kind later (e.g. a raw URL to a
* `marketplace.json`), add a resolver file and insert it before
* {@link resolveUrlSource} (the catch-all, which always matches).
*/
const RESOLVERS = [resolveDirectorySource, resolveUrlSource];
/**
* Resolve a user-supplied `<source>` string into a {@link MarketplaceSource}.
* Tries each resolver in order; the url resolver is the total fallback.
*/
async function resolveSource(input) {
	for (const resolver of RESOLVERS) {
		const result = await resolver(input);
		if (result) return result;
	}
	throw new Error(`Could not resolve marketplace source: "${input}"`);
}
//#endregion
//#region src/marketplace/marketplace_registry.ts
let _initClass$10;
function _applyDecs$10(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$10(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$10(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$10(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$10(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$10(t) {
	var i = _toPrimitive$10(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$10(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$10(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$10(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
/** A registered marketplace plus the name it is keyed by. */
/** Options for {@link MarketplaceRegistry.add}. */
/** A registered marketplace plus the number of plugins its catalog advertises. */
/** The outcome of a successful `refresh()`. */
/** Where a catalog plugin is currently installed. */
/** A catalog plugin entry joined with where it is currently installed. */
const SCOPE_ORDER = [
	"user",
	"project",
	"local"
];
/** Manages the set of known marketplaces and their catalogs. */
const MarketplaceRegistry = createInterfaceId("PluginMarketplaceRegistry");
let _DefaultMarketplaceRe;
(class {
	static {
		[_DefaultMarketplaceRe, _initClass$10] = _applyDecs$10(this, [Implements(MarketplaceRegistry), Service({
			dependencies: [
				Logger,
				KnownMarketplacesStore,
				MarketplaceFetcher,
				PluginRegistry,
				InstalledPluginsStore
			],
			lifetime: ServiceLifetime.Singleton
		})], []).c;
	}
	#logger;
	#store;
	#fetcher;
	#pluginRegistry;
	#installedPlugins;
	constructor(logger, store, fetcher, pluginRegistry, installedPlugins) {
		this.#logger = withPrefix(logger, "[PluginMarketplaceRegistry]");
		this.#store = store;
		this.#fetcher = fetcher;
		this.#pluginRegistry = pluginRegistry;
		this.#installedPlugins = installedPlugins;
	}
	async add(source, options) {
		const resolved = await resolveSource(source);
		this.#logger.debug(`Source "${source}" resolved to ${JSON.stringify(resolved)}`);
		const { catalog, installLocation, installedRevision } = await this.#fetcher.fetch(resolved);
		const existingEntry = await this.#store.get(catalog.name);
		const autoUpdate = options?.autoUpdate ?? existingEntry?.autoUpdate;
		const marketplace = {
			source: resolved,
			lastUpdated: (/* @__PURE__ */ new Date()).toISOString(),
			installLocation,
			installedRevision,
			...autoUpdate ? { autoUpdate: true } : {}
		};
		await this.#store.upsert(catalog.name, marketplace);
		this.#logger.info(`Added marketplace ${catalog.name} (${catalog.plugins.length} plugins)`);
		return {
			name: catalog.name,
			marketplace
		};
	}
	async list() {
		const entries = Object.entries(await this.#store.load());
		return Promise.all(entries.map(async ([name, marketplace]) => ({
			name,
			marketplace,
			pluginCount: await this.#countPlugins(marketplace.installLocation)
		})));
	}
	async refresh(name) {
		const entry = await this.#store.get(name);
		if (!entry) throw new MarketplaceNotFoundError(name);
		const { installLocation, installedRevision } = await this.#fetcher.fetch(entry.source, name);
		const marketplace = {
			...entry,
			installLocation,
			installedRevision,
			lastUpdated: (/* @__PURE__ */ new Date()).toISOString()
		};
		await this.#store.upsert(name, marketplace);
		this.#logger.info(`Refreshed marketplace ${name}`);
		return {
			name,
			marketplace,
			previousRevision: entry.installedRevision
		};
	}
	async show(name) {
		const entry = await this.#store.get(name);
		if (!entry) throw new MarketplaceNotFoundError(name);
		const catalog = await this.#fetcher.readInstalledCatalog(entry.installLocation);
		const ledger = await this.#installedPlugins.load();
		return catalog.plugins.map((plugin) => {
			const installs = [...ledger[`${plugin.name}@${name}`] ?? []].sort((a, b) => {
				const scopeDiff = SCOPE_ORDER.indexOf(a.scope) - SCOPE_ORDER.indexOf(b.scope);
				if (scopeDiff !== 0) return scopeDiff;
				return (a.projectPath ?? "").localeCompare(b.projectPath ?? "");
			}).map((record) => ({
				scope: record.scope,
				projectPath: record.projectPath,
				version: record.version
			}));
			return {
				name: plugin.name,
				description: plugin.description,
				version: plugin.version,
				installs
			};
		}).sort((a, b) => a.name.localeCompare(b.name));
	}
	async remove(name) {
		const entry = await this.#store.get(name);
		if (!entry) throw new MarketplaceNotFoundError(name);
		await this.#cascadeUninstallPlugins(name);
		await this.#deleteCatalogDir(entry.installLocation);
		await this.#store.remove(name);
	}
	async #cascadeUninstallPlugins(name) {
		const ledger = await this.#installedPlugins.load();
		const matchingEntries = Object.entries(ledger).filter(([id]) => {
			try {
				return parseSpecifier(id).marketplace === name;
			} catch {
				return false;
			}
		});
		for (const [id, records] of matchingEntries) for (const record of records) try {
			await this.#pluginRegistry.uninstall(id, record.scope, record.projectPath);
		} catch (e) {
			if (e instanceof PluginNotInstalledError) this.#logger.warn(`Plugin ${id} (${record.scope}) already uninstalled during cascade`);
			else throw e;
		}
	}
	async #deleteCatalogDir(installLocation) {
		if (!installLocation || !isAbsolute(installLocation)) {
			this.#logger.warn(`Refusing to delete catalog dir with invalid installLocation: ${JSON.stringify(installLocation)}`);
			return;
		}
		const root = getMarketplacesDir();
		let resolvedRoot;
		let resolvedInstall;
		try {
			resolvedRoot = await realpath(root);
		} catch {
			resolvedRoot = root;
		}
		try {
			resolvedInstall = await realpath(installLocation);
		} catch (e) {
			if (typeof e === "object" && e !== null && "code" in e && e.code === "ENOENT") return;
			throw e;
		}
		if (resolvedInstall === resolvedRoot) {
			this.#logger.warn(`Refusing to delete catalog dir that equals the marketplaces root: ${resolvedInstall}`);
			return;
		}
		if (!isWithin(resolvedRoot, resolvedInstall)) {
			this.#logger.warn(`Refusing to delete catalog dir outside ${resolvedRoot}: ${resolvedInstall}`);
			return;
		}
		await rm(resolvedInstall, {
			recursive: true,
			force: true
		});
	}
	async #countPlugins(installLocation) {
		try {
			return (await this.#fetcher.readInstalledCatalog(installLocation)).plugins.length;
		} catch (e) {
			this.#logger.warn(`Could not read catalog at ${installLocation} for plugin count`, e instanceof Error ? e : new Error(String(e)));
			return;
		}
	}
	static {
		_initClass$10();
	}
});
//#endregion
//#region src/marketplace/official_marketplaces.ts
/** A marketplace pre-registered automatically, without the user running `marketplace add`. */
/**
* GitLab-maintained marketplaces pre-registered once per machine — see
* {@link OfficialMarketplaceSeeder} for when and how.
*/
const OFFICIAL_MARKETPLACES = [{
	name: "gitlab-duo-plugins",
	source: "https://gitlab.com/gitlab-org/ai/gitlab-duo-plugins"
}];
//#endregion
//#region src/marketplace/official_marketplace_seeder.ts
let _initClass$9;
function _applyDecs$9(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$9(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$9(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$9(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$9(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$9(t) {
	var i = _toPrimitive$9(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$9(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$9(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$9(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
/**
* Pre-registers GitLab's official marketplaces (see {@link OFFICIAL_MARKETPLACES})
* the first time the plugin subsystem runs on a machine.
*
* Gated on `known_marketplaces.json` existing at all, not on whether the
* official marketplace specifically is present. That's what makes removal
* stick: `duo plugin marketplace remove` deletes the entry but leaves the
* (now empty, or otherwise populated) file in place, so a later launch never
* re-seeds it.
*
* Never throws — a failed seed (e.g. offline on first run) is logged and
* retried on the next launch, since the file is only written once `add()`
* actually succeeds.
*/
const OfficialMarketplaceSeeder = createInterfaceId("PluginMarketplaceOfficialSeeder");
let _DefaultOfficialMarke;
(class {
	static {
		[_DefaultOfficialMarke, _initClass$9] = _applyDecs$9(this, [Implements(OfficialMarketplaceSeeder), Service({
			dependencies: [
				Logger,
				KnownMarketplacesStore,
				MarketplaceRegistry
			],
			lifetime: ServiceLifetime.Singleton
		})], []).c;
	}
	#logger;
	#knownMarketplaces;
	#registry;
	constructor(logger, knownMarketplaces, registry) {
		this.#logger = withPrefix(logger, "[OfficialMarketplaceSeeder]");
		this.#knownMarketplaces = knownMarketplaces;
		this.#registry = registry;
	}
	async seed() {
		let alreadyInitialized;
		try {
			alreadyInitialized = await this.#knownMarketplaces.exists();
		} catch (e) {
			this.#logger.warn("Failed to check known_marketplaces.json; skipping official marketplace seeding", e instanceof Error ? e : new Error(String(e)));
			return;
		}
		if (alreadyInitialized) return;
		for (const official of OFFICIAL_MARKETPLACES) await this.#seedOne(official);
	}
	async #seedOne(official) {
		try {
			const registered = await this.#registry.add(official.source, { autoUpdate: true });
			this.#logger.info(`Pre-registered official marketplace ${registered.name}`);
		} catch (e) {
			this.#logger.warn(`Failed to pre-register official marketplace ${official.name}`, e instanceof Error ? e : new Error(String(e)));
		}
	}
	static {
		_initClass$9();
	}
});
//#endregion
//#region src/plugin/enabled_plugin_roots.ts
let _initClass$8;
function _applyDecs$8(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$8(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$8(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$8(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$8(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$8(t) {
	var i = _toPrimitive$8(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$8(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$8(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$8(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
/** An enabled, installed plugin's root, visible in the given workspace context. */
/**
* Resolves which installed plugins are visible and enabled for a given
* workspace context, and picks the install root each should contribute from.
*/
const EnabledPluginRootsResolver = createInterfaceId("PluginMarketplaceEnabledPluginRootsResolver");
function toError$3(e) {
	return e instanceof Error ? e : new Error(String(e));
}
/** Most specific scope first: a project/local install shadows a user-scope one for the same id. */
const SCOPE_PRIORITY = [
	"local",
	"project",
	"user"
];
/** Picks the most specific-scope record, keeping the first when scopes tie. */
function pickMostSpecific(records) {
	let best = records[0];
	let bestRank = SCOPE_PRIORITY.indexOf(best.scope);
	for (const record of records.slice(1)) {
		const rank = SCOPE_PRIORITY.indexOf(record.scope);
		if (rank < bestRank) {
			best = record;
			bestRank = rank;
		}
	}
	return best;
}
let _DefaultEnabledPlugin;
(class {
	static {
		[_DefaultEnabledPlugin, _initClass$8] = _applyDecs$8(this, [Implements(EnabledPluginRootsResolver), Service({
			dependencies: [
				Logger,
				InstalledPluginsStore,
				PluginsConfigStore
			],
			lifetime: ServiceLifetime.Singleton
		})], []).c;
	}
	#logger;
	#store;
	#config;
	constructor(logger, store, config) {
		this.#logger = withPrefix(logger, "[EnabledPluginRootsResolver]");
		this.#store = store;
		this.#config = config;
	}
	async resolve(workspacePaths) {
		const resolvedWorkspaces = workspacePaths.map((path) => resolve(path));
		const ledger = await this.#store.load();
		const roots = (await Promise.all(Object.entries(ledger).map(([id, records]) => this.#rootForId(id, records, resolvedWorkspaces)))).filter((root) => root !== null);
		roots.sort((a, b) => a.pluginId.localeCompare(b.pluginId));
		return roots;
	}
	async #rootForId(id, records, resolvedWorkspaces) {
		const visible = records.filter((record) => this.#isVisible(record, resolvedWorkspaces));
		if (visible.length === 0) return null;
		const relevantWorkspaces = visible.some((record) => record.scope === "user") ? resolvedWorkspaces : resolvedWorkspaces.filter((workspacePath) => visible.some((record) => record.projectPath !== void 0 && resolve(record.projectPath) === workspacePath));
		if (!await this.#isEnabled(id, relevantWorkspaces)) return null;
		let pluginName;
		try {
			pluginName = parseSpecifier(id).plugin;
		} catch (e) {
			this.#logger.warn(`Could not parse plugin id "${id}", skipping`, toError$3(e));
			return null;
		}
		const record = pickMostSpecific(visible);
		return {
			pluginId: id,
			pluginName,
			installPath: record.installPath,
			version: record.version
		};
	}
	#isVisible(record, resolvedWorkspaces) {
		if (record.scope === "user") return true;
		return record.projectPath !== void 0 && resolvedWorkspaces.includes(resolve(record.projectPath));
	}
	async #isEnabled(id, resolvedWorkspaces) {
		if (resolvedWorkspaces.length === 0) return this.#existingEnabledSetting(id, "user", void 0);
		return (await Promise.all(resolvedWorkspaces.map((workspacePath) => this.#existingEnabledSetting(id, "local", workspacePath)))).some((enabled) => enabled);
	}
	async #existingEnabledSetting(id, scope, workspacePath) {
		try {
			return await this.#config.getExistingEnabledSetting(id, scope, workspacePath) ?? true;
		} catch (e) {
			this.#logger.warn(`Could not resolve enabled state for ${id}, defaulting to enabled`, toError$3(e));
			return true;
		}
	}
	static {
		_initClass$8();
	}
});
//#endregion
//#region src/plugin/plugin_cache_sweeper.ts
let _initClass$7;
function _applyDecs$7(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$7(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$7(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$7(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$7(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$7(t) {
	var i = _toPrimitive$7(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$7(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$7(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$7(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
/** Summary of a cache `sweep()` run. */
const PluginCacheSweeper = createInterfaceId("PluginMarketplaceCacheSweeper");
/**
* Name pattern of a version dir renamed aside by `#safeRemoveDir` before its
* recursive `rm`. A dir matching this is already condemned: a leftover from an
* `rm` that was interrupted mid-delete, so a later sweep removes it
* immediately, with no grace period.
*/
const DELETED_DIR_PATTERN = /\.deleted-\d+-\d+$/;
let _DefaultPluginCacheSw;
(class {
	static {
		[_DefaultPluginCacheSw, _initClass$7] = _applyDecs$7(this, [Implements(PluginCacheSweeper), Service({
			dependencies: [
				Logger,
				InstalledPluginsStore,
				SafePluginFs
			],
			lifetime: ServiceLifetime.Singleton
		})], []).c;
	}
	#logger;
	#store;
	#safeFs;
	constructor(logger, store, safeFs) {
		this.#logger = withPrefix(logger, "[PluginCacheSweeper]");
		this.#store = store;
		this.#safeFs = safeFs;
	}
	async sweep() {
		const root = getPluginStoreRoot();
		if (!await pathExists(root)) return {
			removed: [],
			marked: []
		};
		const referenced = referencedInstallPaths(await this.#store.load());
		const now = Date.now();
		const removed = [];
		const marked = [];
		for await (const versionDir of this.#walkVersionDirs(root)) {
			const outcome = await this.#sweepVersionDir(versionDir, referenced, now);
			if (outcome === "removed") removed.push(versionDir);
			else if (outcome === "marked") marked.push(versionDir);
		}
		await this.#pruneEmptyParents(removed);
		this.#logger.debug(`Cache sweep complete: removed ${removed.length}, marked ${marked.length}`);
		return {
			removed,
			marked
		};
	}
	async #sweepVersionDir(versionDir, referenced, now) {
		try {
			if (referenced.has(resolve(versionDir))) {
				await this.#safeFs.clearOrphanMarker(versionDir);
				return "kept";
			}
			if (DELETED_DIR_PATTERN.test(basename(versionDir))) return await this.#safeRemoveDir(versionDir) ? "removed" : "kept";
			const markedAt = await this.#safeFs.readOrphanMarkerTime(versionDir);
			if (markedAt === void 0) {
				await this.#safeFs.writeOrphanMarker(versionDir);
				return "marked";
			}
			if (now - markedAt > 6048e5) return await this.#safeRemoveDir(versionDir) ? "removed" : "kept";
			return "kept";
		} catch (e) {
			this.#logger.warn(`Failed to sweep version dir ${versionDir}`, e instanceof Error ? e : new Error(String(e)));
			return "kept";
		}
	}
	/** Yields every `<root>/<marketplace>/<plugin>/<version>` dir, one at a time. */
	async *#walkVersionDirs(root) {
		for (const mkt of await this.#readDirNames(root)) {
			const mktDir = join(root, mkt);
			for (const plugin of await this.#readDirNames(mktDir)) {
				const pluginDir = join(mktDir, plugin);
				for (const version of await this.#readDirNames(pluginDir)) yield join(pluginDir, version);
			}
		}
	}
	async #readDirNames(dir) {
		try {
			return (await readdir(dir, { withFileTypes: true })).filter((entry) => entry.isDirectory()).map((entry) => entry.name);
		} catch {
			return [];
		}
	}
	async #safeRemoveDir(dir) {
		const root = getPluginStoreRoot();
		const resolvedRoot = await realpath(root).catch(() => root);
		let realDir;
		try {
			realDir = await this.#safeFs.assertWithinStore(dir);
		} catch (e) {
			if (typeof e === "object" && e !== null && "code" in e && e.code === "ENOENT") return false;
			const message = e instanceof Error ? e.message : String(e);
			this.#logger.warn(`Refusing to delete version dir: ${message}`);
			return false;
		}
		if (realDir === resolvedRoot) {
			this.#logger.warn(`Refusing to delete version dir that equals the store root: ${realDir}`);
			return false;
		}
		if (DELETED_DIR_PATTERN.test(basename(realDir))) {
			await rm(realDir, {
				recursive: true,
				force: true
			});
			return true;
		}
		const deletedAside = join(dirname(realDir), `${basename(realDir)}.deleted-${process.pid}-${Date.now()}`);
		await rename(realDir, deletedAside);
		await rm(deletedAside, {
			recursive: true,
			force: true
		});
		return true;
	}
	async #pruneEmptyParents(removed) {
		const root = getPluginStoreRoot();
		const prunable = /* @__PURE__ */ new Set();
		for (const versionDir of removed) {
			let parent = dirname(versionDir);
			while (isWithin(root, parent) && parent !== root) {
				prunable.add(parent);
				parent = dirname(parent);
			}
		}
		const ordered = [...prunable].sort((a, b) => b.length - a.length);
		for (const dir of ordered) await this.#pruneIfEmpty(dir);
	}
	async #pruneIfEmpty(dir) {
		try {
			if ((await readdir(dir)).length === 0) await rmdir(dir);
		} catch (e) {
			if (e.code !== "ENOENT") this.#logger.warn(`Failed to prune directory ${dir}`, e instanceof Error ? e : new Error(String(e)));
		}
	}
	static {
		_initClass$7();
	}
});
//#endregion
//#region src/plugin/plugin_source_resolver.ts
let _initClass$6;
function _applyDecs$6(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$6(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$6(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$6(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$6(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$6(t) {
	var i = _toPrimitive$6(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$6(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$6(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$6(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
/**
* Resolves a catalog plugin entry's `source` to a local directory on disk.
* Relative (string) sources are fully supported and containment-checked against
* the catalog directory. Object sources (github/url/git-subdir/npm) are not yet
* supported and throw.
*/
const PluginSourceResolver = createInterfaceId("PluginMarketplaceSourceResolver");
function splitSegments(value) {
	return value.replace(/^\.\//, "").split(/[/\\]/).filter((segment) => segment.length > 0);
}
let _DefaultPluginSourceR;
(class {
	static {
		[_DefaultPluginSourceR, _initClass$6] = _applyDecs$6(this, [Implements(PluginSourceResolver), Service({
			dependencies: [Logger],
			lifetime: ServiceLifetime.Singleton
		})], []).c;
	}
	#logger;
	constructor(logger) {
		this.#logger = withPrefix(logger, "[PluginSourceResolver]");
	}
	async resolve(source, catalogDir, pluginRoot) {
		if (typeof source === "string") return this.#resolveRelative(source, catalogDir, pluginRoot);
		return this.#rejectObjectSource(source);
	}
	async #resolveRelative(source, catalogDir, pluginRoot) {
		const realCatalogDir = await realpath(catalogDir);
		const segments = [...pluginRoot ? splitSegments(pluginRoot) : [], ...splitSegments(source)];
		for (const segment of segments) assertPathSegment(segment, "plugin source subdir");
		const joined = resolve(realCatalogDir, ...segments);
		const realJoined = await pathExists(joined) ? await realpath(joined) : joined;
		if (!isWithin(realCatalogDir, realJoined)) throw new Error(`Plugin source ${source} escapes catalog directory ${realCatalogDir}`);
		if (!await pathExists(realJoined)) throw new Error(`Plugin source directory does not exist: ${realJoined}`);
		return realJoined;
	}
	#rejectObjectSource(source) {
		const detail = objectSourceDetail(source);
		throw new Error(`Plugin source kind "${source.source}" (${detail}) is not yet supported`);
	}
	async readPluginManifest(pluginDir) {
		const candidates = [join(pluginDir, "plugin.json"), join(pluginDir, ".claude-plugin", "plugin.json")];
		const found = (await Promise.all(candidates.map(async (candidate) => await pathExists(candidate) ? candidate : null))).find((candidate) => candidate !== null);
		if (!found) return {};
		try {
			const raw = await readFile(found, "utf8");
			return parsePluginManifest(JSON.parse(raw));
		} catch (e) {
			this.#logger.warn(`Failed to read plugin manifest at ${found}`, e instanceof Error ? e : new Error(String(e)));
			return {};
		}
	}
	static {
		_initClass$6();
	}
});
function objectSourceDetail(source) {
	switch (source.source) {
		case "github": return source.repo;
		case "url":
		case "git-subdir": return source.url;
		case "npm": return source.package;
		default: return assertNever(source);
	}
}
function assertNever(value) {
	throw new Error(`Unexpected plugin source: ${JSON.stringify(value)}`);
}
//#endregion
//#region src/plugin/plugin_installer.ts
let _initClass$5;
function _applyDecs$5(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$5(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$5(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$5(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$5(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$5(t) {
	var i = _toPrimitive$5(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$5(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$5(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$5(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
/**
* The version segment used when a plugin declares no version anywhere. Matches
* Claude Code's behaviour: unversioned plugins share a single `unknown` cache
* dir and are re-copied from source on every install, since there is no version
* to key the cache on.
*/
const UNVERSIONED = "unknown";
/** The outcome of a successful install: the ledger id and the stored record. */
/** The outcome of materialising a plugin's files into the shared cache. */
/** Installs a marketplace plugin into a scope's ledger and the shared store. */
const PluginInstaller = createInterfaceId("PluginMarketplaceInstaller");
/**
* The `defaultEnabled` fallback for a freshly installed plugin: the marketplace
* entry's value takes precedence over the plugin manifest's, defaulting to
* enabled when neither declares one. Only consulted when the user has no
* existing enabled setting.
*/
function resolveDefaultEnabled(entryDefaultEnabled, manifestDefaultEnabled) {
	return entryDefaultEnabled ?? manifestDefaultEnabled ?? true;
}
let _DefaultPluginInstall;
(class {
	static {
		[_DefaultPluginInstall, _initClass$5] = _applyDecs$5(this, [Implements(PluginInstaller), Service({
			dependencies: [
				Logger,
				KnownMarketplacesStore,
				MarketplaceFetcher,
				SafePluginFs,
				PluginSourceResolver,
				InstalledPluginsStore,
				PluginsConfigStore
			],
			lifetime: ServiceLifetime.Singleton
		})], []).c;
	}
	#logger;
	#marketplaces;
	#fetcher;
	#safeFs;
	#resolver;
	#store;
	#pluginsConfig;
	constructor(logger, marketplaces, fetcher, safeFs, resolver, store, pluginsConfig) {
		this.#logger = withPrefix(logger, "[PluginInstaller]");
		this.#marketplaces = marketplaces;
		this.#fetcher = fetcher;
		this.#safeFs = safeFs;
		this.#resolver = resolver;
		this.#store = store;
		this.#pluginsConfig = pluginsConfig;
	}
	async install(spec, scope, workspacePath) {
		const id = `${spec.plugin}@${spec.marketplace}`;
		const projectPath = projectPathForScope(scope, workspacePath);
		if (await this.#store.get(id, scope, projectPath)) throw new Error(`Plugin ${id} is already installed in ${scope} scope`);
		const { version, installDir, defaultEnabled } = await this.materialize(spec);
		const now = (/* @__PURE__ */ new Date()).toISOString();
		const record = {
			installedAt: now,
			installPath: installDir,
			lastUpdated: now,
			scope,
			version,
			...projectPath !== void 0 ? { projectPath } : {}
		};
		await this.#store.upsert(id, record);
		const enabled = await this.#pluginsConfig.getExistingEnabledSetting(id, scope, workspacePath) ?? defaultEnabled;
		await this.#pluginsConfig.setEnabled(id, enabled, scope, workspacePath);
		return {
			id,
			scope,
			record
		};
	}
	async materialize(spec) {
		const marketplace = await this.#marketplaces.get(spec.marketplace);
		if (!marketplace) throw new Error(`Unknown marketplace "${spec.marketplace}". Run: duo plugin marketplace add <source>`);
		const catalog = await this.#fetcher.readInstalledCatalog(marketplace.installLocation);
		const entry = catalog.plugins.find((plugin) => plugin.name === spec.plugin);
		if (!entry) {
			const available = catalog.plugins.map((plugin) => plugin.name).join(", ") || "(none)";
			throw new Error(`Plugin "${spec.plugin}" not found in marketplace "${spec.marketplace}". Available: ${available}`);
		}
		const id = `${spec.plugin}@${spec.marketplace}`;
		const pluginSrcDir = await this.#resolver.resolve(entry.source, marketplace.installLocation, catalog.metadata?.pluginRoot);
		const manifest = await this.#resolver.readPluginManifest(pluginSrcDir);
		const version = this.#resolveVersion(id, manifest.version ?? entry.version);
		assertPathSegment(version, "plugin version");
		const installDir = getPluginInstallDir(spec.marketplace, spec.plugin, version);
		if (version !== "unknown" && await pathExists(installDir)) {
			await this.#safeFs.assertWithinStore(installDir);
			this.#logger.info(`Reusing cached ${id}@${version} at ${installDir}`);
		} else {
			await this.#safeFs.placePluginFiles(pluginSrcDir, installDir, { marketplaceRoot: marketplace.installLocation });
			this.#logger.info(`Materialised ${id}@${version} into ${installDir}`);
		}
		await this.#safeFs.clearOrphanMarker(installDir);
		return {
			version,
			installDir,
			defaultEnabled: resolveDefaultEnabled(entry.defaultEnabled, manifest.defaultEnabled)
		};
	}
	#resolveVersion(id, declared) {
		if (declared) return declared.replace(/\+.*$/, "");
		this.#logger.warn(`Plugin ${id} declares no version; using "${UNVERSIONED}" (cache cannot be shared)`);
		return UNVERSIONED;
	}
	static {
		_initClass$5();
	}
});
//#endregion
//#region src/plugin/plugin_updater.ts
let _initClass$4;
function _applyDecs$4(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$4(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$4(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$4(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$4(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$4(t) {
	var i = _toPrimitive$4(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$4(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$4(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$4(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
/** The outcome of a successful `update()`. */
/** Options for {@link PluginUpdater.update}. */
/**
* Updates an already-installed plugin across every scope/workspace it is
* recorded in: by default refreshes the owning marketplace's catalog (unless
* skipped via {@link UpdateOptions.skipRefresh}), re-materialises the plugin's
* files, and moves every ledger record for it onto the new version.
*
* Not atomic across scopes: a failure partway may leave records on mixed
* versions. Self-heals on retry - re-running the update moves the
* stragglers forward.
*/
const PluginUpdater = createInterfaceId("PluginMarketplaceUpdater");
let _DefaultPluginUpdater;
(class {
	static {
		[_DefaultPluginUpdater, _initClass$4] = _applyDecs$4(this, [Implements(PluginUpdater), Service({
			dependencies: [
				Logger,
				InstalledPluginsStore,
				MarketplaceRegistry,
				PluginInstaller,
				PluginCacheOrphaner
			],
			lifetime: ServiceLifetime.Singleton
		})], []).c;
	}
	#logger;
	#store;
	#marketplaces;
	#installer;
	#cacheOrphaner;
	constructor(logger, store, marketplaces, installer, cacheOrphaner) {
		this.#logger = withPrefix(logger, "[PluginUpdater]");
		this.#store = store;
		this.#marketplaces = marketplaces;
		this.#installer = installer;
		this.#cacheOrphaner = cacheOrphaner;
	}
	async update(spec, options) {
		const id = `${spec.plugin}@${spec.marketplace}`;
		const records = (await this.#store.load())[id] ?? [];
		if (records.length === 0) throw new Error(`Plugin ${id} is not installed`);
		if (!options?.skipRefresh) await this.#marketplaces.refresh(spec.marketplace);
		const { version, installDir } = await this.#installer.materialize(spec);
		const previousVersions = [...new Set(records.map((record) => record.version))];
		if (version !== "unknown" && records.every((record) => record.version === version)) return {
			id,
			previousVersions,
			version,
			updated: false
		};
		const oldInstallPaths = new Set(records.map((record) => record.installPath));
		for (const record of records) {
			const updatedRecord = {
				...record,
				version,
				installPath: installDir,
				lastUpdated: (/* @__PURE__ */ new Date()).toISOString()
			};
			await this.#store.upsert(id, updatedRecord);
		}
		if (version === "unknown") this.#logger.info(`Refreshed unversioned plugin ${id} from source across ${records.length} record(s)`);
		else this.#logger.info(`Updated ${id} to ${version} across ${records.length} record(s)`);
		for (const oldPath of oldInstallPaths) if (oldPath !== installDir) await this.#cacheOrphaner.markOrphanedIfUnreferenced(oldPath);
		return {
			id,
			previousVersions,
			version,
			updated: true
		};
	}
	static {
		_initClass$4();
	}
});
//#endregion
//#region src/plugin/plugin_auto_updater.ts
let _initClass$3;
function _applyDecs$3(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$3(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$3(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$3(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$3(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$3(t) {
	var i = _toPrimitive$3(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$3(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$3(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$3(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
/** A plugin that was updated to a new version during a `run()` pass. */
/** The outcome of a `run()` pass. */
/**
* Updates every installed plugin belonging to a marketplace opted into
* `autoUpdate`. Intended to be run in the background (fire-and-forget); never
* throws, reporting failures per-plugin instead.
*/
const PluginAutoUpdater = createInterfaceId("PluginMarketplaceAutoUpdater");
let _DefaultPluginAutoUpd;
(class {
	static {
		[_DefaultPluginAutoUpd, _initClass$3] = _applyDecs$3(this, [Implements(PluginAutoUpdater), Service({
			dependencies: [
				Logger,
				KnownMarketplacesStore,
				InstalledPluginsStore,
				MarketplaceRegistry,
				PluginUpdater
			],
			lifetime: ServiceLifetime.Singleton
		})], []).c;
	}
	#logger;
	#marketplacesStore;
	#installedPlugins;
	#marketplaces;
	#updater;
	constructor(logger, marketplacesStore, installedPlugins, marketplaces, updater) {
		this.#logger = withPrefix(logger, "[PluginAutoUpdater]");
		this.#marketplacesStore = marketplacesStore;
		this.#installedPlugins = installedPlugins;
		this.#marketplaces = marketplaces;
		this.#updater = updater;
	}
	async run() {
		try {
			return await this.#doRun();
		} catch (e) {
			this.#logger.warn("Plugin auto-update failed", e instanceof Error ? e : new Error(String(e)));
			return {
				updated: [],
				failed: []
			};
		}
	}
	async #doRun() {
		const updated = [];
		const failed = [];
		const marketplaces = await this.#marketplacesStore.load();
		const optedIn = Object.keys(marketplaces).filter((name) => marketplaces[name].autoUpdate);
		if (optedIn.length === 0) {
			this.#logger.debug("Skipping auto-update: no marketplaces opted in");
			return {
				updated,
				failed
			};
		}
		this.#logger.debug(`Running auto-update for ${optedIn.length} opted-in marketplace(s)`);
		const idsByMarketplace = await this.#groupInstalledIdsByMarketplace(optedIn);
		if (idsByMarketplace.size === 0) {
			this.#logger.debug("Skipping auto-update: no installed plugins from opted-in marketplaces");
			return {
				updated,
				failed
			};
		}
		for (const [marketplace, ids] of idsByMarketplace) if (await this.#tryRefresh(marketplace)) {
			const outcome = await this.#updateAll(ids);
			updated.push(...outcome.updated);
			failed.push(...outcome.failed);
		} else failed.push(...ids);
		if (updated.length > 0) {
			const summary = updated.map((plugin) => `${plugin.id} (${plugin.version})`).join(", ");
			this.#logger.info(`Auto-updated ${updated.length} plugin(s): ${summary}`);
		}
		return {
			updated,
			failed
		};
	}
	async #groupInstalledIdsByMarketplace(optedIn) {
		const ledger = await this.#installedPlugins.load();
		const optedInSet = new Set(optedIn);
		const idsByMarketplace = /* @__PURE__ */ new Map();
		for (const id of Object.keys(ledger)) {
			const marketplace = this.#parseMarketplaceName(id);
			if (marketplace && optedInSet.has(marketplace)) {
				const ids = idsByMarketplace.get(marketplace) ?? [];
				ids.push(id);
				idsByMarketplace.set(marketplace, ids);
			}
		}
		return idsByMarketplace;
	}
	#parseMarketplaceName(id) {
		try {
			return parseSpecifier(id).marketplace;
		} catch {
			this.#logger.debug(`Skipping ledger entry with invalid specifier: ${JSON.stringify(id)}`);
			return;
		}
	}
	async #updateAll(ids) {
		const updated = [];
		const failed = [];
		for (const id of ids) {
			const result = await this.#tryUpdate(id);
			if (result === void 0) failed.push(id);
			else if (result.updated) updated.push({
				id: result.id,
				version: result.version
			});
		}
		return {
			updated,
			failed
		};
	}
	async #tryRefresh(marketplace) {
		try {
			await this.#marketplaces.refresh(marketplace);
			return true;
		} catch (e) {
			this.#logger.warn(`Failed to refresh marketplace ${marketplace} for auto-update`, e instanceof Error ? e : new Error(String(e)));
			return false;
		}
	}
	async #tryUpdate(id) {
		try {
			const spec = parseSpecifier(id);
			return await this.#updater.update(spec, { skipRefresh: true });
		} catch (e) {
			this.#logger.warn(`Failed to auto-update plugin ${id}`, e instanceof Error ? e : new Error(String(e)));
			return;
		}
	}
	static {
		_initClass$3();
	}
});
//#endregion
//#region src/plugin/plugin_commands_reader.ts
let _initClass$2;
function _applyDecs$2(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$2(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$2(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$2(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$2(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$2(t) {
	var i = _toPrimitive$2(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$2(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$2(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$2(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
const COMMANDS_DIR_NAME = "commands";
const COMMAND_FILE_SUFFIX = ".md";
function isEnoent$2(e) {
	return e instanceof Error && e.code === "ENOENT";
}
function toError$2(e) {
	return e instanceof Error ? e : new Error(String(e));
}
/**
* Discovers `commands/*.md` files contributed by enabled, installed plugins.
* Implements the `PluginCommandsSource` port owned by ai-context; parsing the
* files (frontmatter, body) is the consumer's job — this reader only resolves
* which files exist and that they live inside the plugin store.
*/
let _DefaultPluginCommand;
(class {
	static {
		[_DefaultPluginCommand, _initClass$2] = _applyDecs$2(this, [Implements(PluginCommandsSource), Service({
			dependencies: [
				Logger,
				EnabledPluginRootsResolver,
				SafePluginFs
			],
			lifetime: ServiceLifetime.Singleton
		})], []).c;
	}
	contributesCommands = true;
	#logger;
	#roots;
	#safeFs;
	constructor(logger, roots, safeFs) {
		this.#logger = withPrefix(logger, "[PluginCommandsReader]");
		this.#roots = roots;
		this.#safeFs = safeFs;
	}
	async listCommandFiles(workspacePaths) {
		const installs = await this.#roots.resolve(workspacePaths);
		const entries = (await Promise.all(installs.map((install) => this.#readCommands(install.pluginId, install.pluginName, install.installPath, install.version)))).flat();
		entries.sort((a, b) => {
			const idDiff = a.pluginId.localeCompare(b.pluginId);
			if (idDiff !== 0) return idDiff;
			return a.commandName.localeCompare(b.commandName);
		});
		return entries;
	}
	async #readCommands(id, pluginName, installPath, version) {
		let realInstallDir;
		try {
			realInstallDir = await this.#safeFs.assertWithinStore(installPath);
		} catch (e) {
			if (isEnoent$2(e)) this.#logger.debug(`Install dir for ${id} does not exist, skipping`);
			else this.#logger.warn(`Could not verify install dir for ${id}, skipping`, toError$2(e));
			return [];
		}
		try {
			return await this.#scanCommandsDir(id, pluginName, realInstallDir, version);
		} catch (e) {
			this.#logger.warn(`Could not read commands for ${id}, skipping`, toError$2(e));
			return [];
		}
	}
	async #scanCommandsDir(id, pluginName, realInstallDir, version) {
		const commandsDir = join(realInstallDir, COMMANDS_DIR_NAME);
		let dirents;
		try {
			dirents = await readdir(commandsDir, { withFileTypes: true });
		} catch (e) {
			if (isEnoent$2(e)) return [];
			throw e;
		}
		const candidateNames = dirents.filter((dirent) => dirent.name.endsWith(COMMAND_FILE_SUFFIX) && (dirent.isFile() || dirent.isSymbolicLink())).map((dirent) => dirent.name);
		return (await Promise.all(candidateNames.map((fileName) => this.#readCommandEntry(id, pluginName, realInstallDir, fileName, version).catch((e) => {
			this.#logger.warn(`Could not read command "${fileName}" for ${id}, skipping`, toError$2(e));
			return null;
		})))).filter((entry) => entry !== null);
	}
	async #readCommandEntry(id, pluginName, realInstallDir, fileName, version) {
		const commandFilePath = join(realInstallDir, COMMANDS_DIR_NAME, fileName);
		let realFilePath;
		try {
			realFilePath = await realpath(commandFilePath);
			if (!isWithin(realInstallDir, realFilePath)) {
				this.#logger.warn(`Skipping command "${fileName}" for ${id}: it resolves outside the plugin install dir`);
				return null;
			}
			if (!(await stat(realFilePath)).isFile()) return null;
		} catch (e) {
			if (isEnoent$2(e)) return null;
			throw e;
		}
		const commandName = basename(fileName, COMMAND_FILE_SUFFIX);
		return {
			pluginId: id,
			pluginName,
			commandName,
			commandFilePath: realFilePath,
			displayPath: `${id}@${version}/commands/${commandName}`
		};
	}
	static {
		_initClass$2();
	}
});
//#endregion
//#region src/plugin/plugin_components_reader.ts
let _initClass$1;
function _applyDecs$1(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$1(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$1(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$1(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$1(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$1(t) {
	var i = _toPrimitive$1(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$1(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$1(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$1(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
const MCP_CONFIG_FILE_NAME = ".mcp.json";
/** An MCP server config contributed by an enabled, installed plugin. */
/** Reads the MCP configs contributed by enabled, installed plugins. */
const PluginComponentsReader = createInterfaceId("PluginMarketplacePluginComponentsReader");
function isEnoent$1(e) {
	return e instanceof Error && e.code === "ENOENT";
}
function toError$1(e) {
	return e instanceof Error ? e : new Error(String(e));
}
let _DefaultPluginCompone;
(class {
	static {
		[_DefaultPluginCompone, _initClass$1] = _applyDecs$1(this, [Implements(PluginComponentsReader), Service({
			dependencies: [
				Logger,
				EnabledPluginRootsResolver,
				SafePluginFs
			],
			lifetime: ServiceLifetime.Singleton
		})], []).c;
	}
	#logger;
	#roots;
	#safeFs;
	constructor(logger, roots, safeFs) {
		this.#logger = withPrefix(logger, "[PluginComponentsReader]");
		this.#roots = roots;
		this.#safeFs = safeFs;
	}
	async listEnabledMcpConfigs(workspacePaths) {
		const roots = await this.#roots.resolve(workspacePaths);
		const configs = (await Promise.all(roots.map((root) => this.#readMcpConfig(root).catch((e) => {
			this.#logger.warn(`Could not read MCP config for ${root.pluginId}, skipping`, toError$1(e));
			return null;
		})))).filter((config) => config !== null);
		configs.sort((a, b) => a.pluginId.localeCompare(b.pluginId));
		return configs;
	}
	async #readMcpConfig(root) {
		let realInstallDir;
		try {
			realInstallDir = await this.#safeFs.assertWithinStore(root.installPath);
		} catch (e) {
			if (isEnoent$1(e)) this.#logger.debug(`Install dir for ${root.pluginId} does not exist, skipping`);
			else this.#logger.warn(`Could not verify install dir for ${root.pluginId}, skipping`, toError$1(e));
			return null;
		}
		const configPath = join(realInstallDir, MCP_CONFIG_FILE_NAME);
		let realConfigPath;
		try {
			realConfigPath = await realpath(configPath);
		} catch (e) {
			if (isEnoent$1(e)) return null;
			this.#logger.warn(`Could not read config for ${root.pluginId} at ${configPath}, skipping`, toError$1(e));
			return null;
		}
		if (!await this.#safeFs.isWithinStore(realConfigPath)) {
			this.#logger.warn(`Skipping config for ${root.pluginId}: its ${MCP_CONFIG_FILE_NAME} escapes the plugin store`);
			return null;
		}
		try {
			if (!(await stat(realConfigPath)).isFile()) return null;
		} catch (e) {
			if (isEnoent$1(e)) return null;
			this.#logger.warn(`Could not read config for ${root.pluginId} at ${realConfigPath}, skipping`, toError$1(e));
			return null;
		}
		return {
			pluginId: root.pluginId,
			pluginName: root.pluginName,
			configPath: realConfigPath,
			pluginRoot: realInstallDir
		};
	}
	static {
		_initClass$1();
	}
});
//#endregion
//#region src/plugin/plugin_skills_reader.ts
let _initClass;
function _applyDecs(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey(t) {
	var i = _toPrimitive(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
const SKILL_FILE_NAME = "SKILL.md";
const SKILLS_DIR_NAME = "skills";
/** A skill file contributed by an enabled, installed plugin. */
function isEnoent(e) {
	return e instanceof Error && e.code === "ENOENT";
}
function toError(e) {
	return e instanceof Error ? e : new Error(String(e));
}
let _DefaultPluginSkillsR;
(class {
	static {
		[_DefaultPluginSkillsR, _initClass] = _applyDecs(this, [Implements(PluginSkillsSource), Service({
			dependencies: [
				Logger,
				EnabledPluginRootsResolver,
				SafePluginFs
			],
			lifetime: ServiceLifetime.Singleton
		})], []).c;
	}
	contributesSkills = true;
	#logger;
	#roots;
	#safeFs;
	constructor(logger, roots, safeFs) {
		this.#logger = withPrefix(logger, "[PluginSkillsReader]");
		this.#roots = roots;
		this.#safeFs = safeFs;
	}
	async listSkillFiles(workspacePaths) {
		const roots = await this.#roots.resolve(workspacePaths);
		const entries = (await Promise.all(roots.map((root) => this.#readSkills(root.pluginId, root.pluginName, root.installPath, root.version)))).flat();
		entries.sort((a, b) => {
			const idDiff = a.pluginId.localeCompare(b.pluginId);
			if (idDiff !== 0) return idDiff;
			return a.skillName.localeCompare(b.skillName);
		});
		return entries;
	}
	async #readSkills(id, pluginName, installPath, version) {
		let realInstallDir;
		try {
			realInstallDir = await this.#safeFs.assertWithinStore(installPath);
		} catch (e) {
			if (isEnoent(e)) this.#logger.debug(`Install dir for ${id} does not exist, skipping`);
			else this.#logger.warn(`Could not verify install dir for ${id}, skipping`, toError(e));
			return [];
		}
		try {
			return await this.#scanSkillsDir(id, pluginName, realInstallDir, version);
		} catch (e) {
			this.#logger.warn(`Could not read skills for ${id}, skipping`, toError(e));
			return [];
		}
	}
	async #scanSkillsDir(id, pluginName, realInstallDir, version) {
		const skillsDir = join(realInstallDir, SKILLS_DIR_NAME);
		let skillDirents;
		try {
			skillDirents = await readdir(skillsDir, { withFileTypes: true });
		} catch (e) {
			if (isEnoent(e)) return [];
			throw e;
		}
		const candidateNames = skillDirents.filter((dirent) => dirent.isDirectory() || dirent.isSymbolicLink()).map((dirent) => dirent.name);
		return (await Promise.all(candidateNames.map((skillName) => this.#readSkillEntry(id, pluginName, skillsDir, skillName, version).catch((e) => {
			this.#logger.warn(`Could not read skill "${skillName}" for ${id}, skipping`, toError(e));
			return null;
		})))).filter((entry) => entry !== null);
	}
	async #readSkillEntry(id, pluginName, skillsDir, skillName, version) {
		const skillFilePath = join(skillsDir, skillName, SKILL_FILE_NAME);
		try {
			if (!(await stat(skillFilePath)).isFile()) return null;
		} catch (e) {
			if (isEnoent(e)) return null;
			throw e;
		}
		return {
			pluginId: id,
			pluginName,
			skillName,
			skillFilePath,
			displayPath: `${id}@${version}/skills/${skillName}`
		};
	}
	static {
		_initClass();
	}
});
//#endregion
//#region src/contributions.ts
const duoPluginMarketplaceContributions = [
	_DefaultKnownMarketpl,
	_DefaultMarketplaceFe,
	_DefaultMarketplaceRe,
	_DefaultOfficialMarke,
	_DefaultSafePluginFs,
	_DefaultPluginSourceR,
	_DefaultInstalledPlug,
	_DefaultPluginsConfig,
	_DefaultPluginInstall,
	_DefaultPluginCacheOr,
	_DefaultPluginCacheSw,
	_DefaultPluginRegistr,
	_DefaultPluginUpdater,
	_DefaultEnabledPlugin,
	_DefaultPluginSkillsR,
	_DefaultPluginCompone,
	_DefaultPluginCommand,
	_DefaultPluginAutoUpd
];
//#endregion
export { AmbiguousPluginNameError, MarketplaceFetcher, MarketplaceRegistry, OfficialMarketplaceSeeder, PluginAutoUpdater, PluginCacheSweeper, PluginComponentsReader, PluginInstaller, PluginNotInstalledError, PluginRegistry, PluginUpdater, UNVERSIONED, duoPluginMarketplaceContributions, parsePluginScope, parseSpecifier };

//# sourceMappingURL=node.mjs.map